import { cx } from 'cvb';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { toggleBodyClass } from '../../../utils/jsUtils';
import { noop } from '../../../utils/noop';
import WithOutsideClick from '../../../utils/WithOutsideClick';
import Loader from '../../Loader/Loader';
import BackgroundOverlay from '../BackgroundOverlay/BackgroundOverlay';
import { DrawerGlobalStyles, DrawerStyles } from './Drawer.style';

const Drawer = ({
  heading,
  icon,
  subHeading,
  children,
  toggleState,
  isLoading,
  onOpen,
  type,
  height,
  disableOutsideClick = false,
  disableDrawerClose = false,
  hideDivider = false,
  autoHeight = false,
  hideCloseButton = false,
  enableAdobeCarousel = false,
}) => {
  const [isDrawerActive, activateDrawer] = toggleState;

  useEffect(() => {
    toggleBodyClass(isDrawerActive, 'drawer-active');
  }, [isDrawerActive]);

  const drawerHeight = useMemo(() => {
    if (autoHeight) return 'auto';
    return height ? `${height}` : `90%`;
  }, [height, autoHeight]);

  useEffect(() => {
    if (isDrawerActive) onOpen();
  }, [isDrawerActive, onOpen]);

  const handleClose = () => {
    if (!disableDrawerClose) {
      activateDrawer(false);
    }
  };

  const handleOutsideClick = () => {
    if (!disableOutsideClick) {
      activateDrawer(false);
    }
  };

  if (!isDrawerActive) {
    return null;
  }

  return createPortal(
    <div>
      <BackgroundOverlay />
      <div
        className={cx(`drawer-container ${type}`, isDrawerActive && 'is-open', autoHeight && 'auto-height')}
        style={
          isDrawerActive && !autoHeight
            ? {
                transform: `translateY(calc(100% - ${drawerHeight}))`,
              }
            : {}
        }
      >
        <div className="drawer-layout">
          <WithOutsideClick onOutsideClick={handleOutsideClick}>
            <div className={`drawer-header ${type}`}>
              <div className="drawer-line" onClick={handleClose} onKeyDown={noop} role="button" tabIndex={0} />
              {heading && (
                <>
                  <div className="title">
                    <div className={`${icon}`} />
                    <div>{heading}</div>
                  </div>

                  {subHeading && <div className="sub-heading">{subHeading}</div>}
                  {!hideDivider && <div className="divider" />}
                </>
              )}
              {!hideCloseButton && (
                <i className="csicon-danger" onKeyDown={noop} role="button" tabIndex={0} onClick={handleClose} />
              )}
            </div>

            {enableAdobeCarousel && (
              <div
                className="modal-body"
                style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', height: 0, overflow: 'hidden' }}
              >
                <div className="header">Producto agregado a tu Carro</div>
              </div>
            )}

            <div className={`drawer-content ${type}`}>
              {children}
              {isLoading && <Loader />}
            </div>
          </WithOutsideClick>
        </div>
        <style jsx>{DrawerStyles}</style>
        <style jsx>{DrawerGlobalStyles}</style>
      </div>
    </div>,
    document.body
  );
};

Drawer.defaultProps = {
  subHeading: null,
  isLoading: true,
  onOpen: noop,
  type: '',
  icon: '',
  height: null,
  disableOutsideClick: false,
  disableDrawerClose: false,
  hideDivider: false,
  autoHeight: false,
  hideCloseButton: false,
  enableAdobeCarousel: false,
};

Drawer.propTypes = {
  children: PropTypes.node.isRequired,
  heading: PropTypes.string.isRequired,
  subHeading: PropTypes.string,
  toggleState: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  onOpen: PropTypes.func,
  type: PropTypes.string,
  icon: PropTypes.string,
  height: PropTypes.string,
  disableOutsideClick: PropTypes.bool,
  disableDrawerClose: PropTypes.bool,
  hideDivider: PropTypes.bool,
  autoHeight: PropTypes.bool,
  hideCloseButton: PropTypes.bool,
  enableAdobeCarousel: PropTypes.bool,
};

export default Drawer;
